import { useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Navigate } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { CircularProgress, Box, Typography } from "@mui/material";
import RegionSelectionModal from "./components/RegionSelectionModal";
import { getUser, createUser } from "./api/User";
import { useAlert } from "./hook/useAlert";
import { UserProvider } from "./context/UserContext";

const RequireAuth = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance } = useMsal();
  const { onError, onSuccess } = useAlert();

  const [userStatus, setUserStatus] = useState({
    loading: true,
    exists: false,
    showRegionModal: false,
  });
  const [regionLoading, setRegionLoading] = useState(false);
  const [regionError, setRegionError] = useState("");

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const checkUserExists = async () => {
    try {
      await getUser();
      setUserStatus({
        loading: false,
        exists: true,
        showRegionModal: false,
      });
      onSuccess("Welcome back!");
    } catch (error) {
      if (error?.response?.status === 404) {
        setUserStatus({
          loading: false,
          exists: false,
          showRegionModal: true,
        });
      } else {
        onError("Authentication error. Please try logging in again.");
        handleLogout();
      }
    }
  };

  const handleRegionSelect = async (region) => {
    setRegionLoading(true);
    setRegionError("");

    try {
      await createUser(region);
      setUserStatus({
        loading: false,
        exists: true,
        showRegionModal: false,
      });
      onSuccess("Account created successfully! Welcome to the platform.");
    } catch (error) {
      if (error?.response?.status === 409) {
        setUserStatus({
          loading: false,
          exists: true,
          showRegionModal: false,
        });
        onSuccess("Welcome back!");
      } else {
        const errorMessage = error?.message || "Failed to create user. Please try again.";
        setRegionError(errorMessage);
        onError(errorMessage);
      }
    } finally {
      setRegionLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !userStatus.exists && !userStatus.showRegionModal) {
      checkUserExists();
    }
  }, [isAuthenticated]);

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1">Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (userStatus.showRegionModal) {
    return (
      <>
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: "background.default",
          }}
        />
        <RegionSelectionModal
          open={userStatus.showRegionModal}
          onRegionSelect={handleRegionSelect}
          loading={regionLoading}
          error={regionError}
        />
      </>
    );
  }

  if (userStatus.exists) {
    return <UserProvider>{children}</UserProvider>;
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export default RequireAuth;
